import * as d3 from 'd3';
import * as moment from 'moment';
import { apdate, apmonth, intcomma } from 'journalize';
import data from '../../data/data.json';
import { swoopyDrag } from './swoopy-drag';
import { wordwrap } from 'd3-jetpack';
import _ from 'lodash';

// a reference to the default graphic container, change if needed
const container = d3.select('#timeline');

// a helper function to grab the container's width
const getFrameWidth = () => container.node().offsetWidth;

let margin = {
  top: 50,
  bottom: 125,
};

let breakPoint = 748;

// apprehensions during the trump administration
let dataTrump = data['Totals Breakdown Trump'];

// change dates to moment date time objects
dataTrump.forEach(d => (d.Month = moment(d.Month, 'MMM YYYY')));

// make sure data is sorted from earliest date to latest date
dataTrump.sort((a, b) => a.Month - b.Month);

// annotation party
let policyData = data['Annotations'].filter(d => d.Type === 'Policy'),
  explanationData = data['Annotations'].filter(d => d.Type === 'Explanation');

// change dates to moment date time objects
policyData.forEach(d => (d.Date = moment(d.Date, 'M/D/YYYY')));
explanationData.forEach(d => (d.Date = moment(d.Date, 'M/D/YYYY')));

let dateRange = d3.extent(dataTrump, d => d.Month),
  totalsMax = d3.max(dataTrump, d => d.Total);

// write a custom tick formatter
let formatYear = d3.timeFormat('%Y');

function customFormat(d) {
  if (moment(d).month() === 0) return formatYear(d);
  else return apmonth(d);
}

// format for x axis on mobile
function xAxisTickFormat(d) {
  let format = d3.format('~s');
  return format(d);
}

// color key for types of apprehensions
let color = {
  'Unaccompanied Minors': '#6bc1bb',
  'Family Units': '#ffc200',
  'Single Adults': '#a0d2de',
};

let svg = container.append('svg'),
  g = svg.append('g').attr('class', 'timeline-chart');

let y = d3.scaleTime().domain(dateRange),
  x = d3.scaleLinear().domain([0, totalsMax]);

let xAxis = g.append('g').attr('class', 'timeline-axis axis axis--x'),
  yAxis = g.append('g').attr('class', 'timeline-axis axis axis--y');

let stack = d3
    .stack()
    .keys(['Unaccompanied Minors', 'Family Units', 'Single Adults']),
  stackedData = stack(dataTrump);

let area = d3.area().y(d => y(d.data.Month));

let stackedArea = g
  .append('g')
  .selectAll('path')
  .data(stackedData)
  .enter()
  .append('path')
  .style('fill', d => color[d.key]);

// policy annotations
let policyGroup = g.append('g').attr('class', 'policy-group');

let policies = policyGroup
  .selectAll('.policy')
  .data(policyData)
  .enter()
  .append('g')
  .attr('class', 'policy');

policies
  .append('line')
  .attr('x1', 0)
  .attr('stroke-width', '1px')
  .attr('stroke', '#666666')
  .attr('stroke-dasharray', '5');

let policyTextGroup = policies
  .append('g')
  .attr('class', 'policy-text-group')
  .attr('transform', d => {
    if (d.Date.isSame(moment('6/7/2019', 'M/D/YYYY')))
      return 'translate(0, -6)';
    if (d.Date.isSame(moment('7/15/2019', 'M/D/YYYY')))
      return 'translate(0, 3)';
  });
// let policyRect = policyTextGroup.append('rect').attr('fill', 'red'); // might add this back to give annotations a white background

let policyDate = policyTextGroup
  .append('text')
  .text(d => {
    // if (d.Date.year().toString() === data.Statistics.Year) {
    //   return `${apmonth(new Date(d.Date))} ${d.Date.day()}`;
    // } else
    return apdate(new Date(d.Date));
  })
  .attr('text-anchor', 'left')
  .attr('font-weight', 700);

let policyText = policyTextGroup
  .append('text')
  .text(d => d.Annotation)
  .attr('text-anchor', 'left');

let labelElements = g
  .selectAll('.label-element')
  .data(dataTrump)
  .enter()
  .append('g')
  .attr('class', 'label-element');

labelElements
  .append('circle')
  .attr('r', 3)
  .attr('fill', 'hsl(0, 0%, 47%)');

labelElements
  .append('text')
  .text(d => intcomma(d.Total))
  .attr('font-size', '12px')
  .attr('fill', 'hsl(0, 0%, 47%)')
  .attr('stroke', 'white')
  .attr('stroke-width', '3px')
  .attr('paint-order', 'stroke fill')
  .attr('dx', 10)
  .attr('dy', 3);

// annotations for swoopyDrag
let annotations = [
  {
    xVal: 0,
    yVal: moment('4/1/2017', 'M/DD/YYYY'),
    path: 'M-8,-40C-40,-50,-72,-44,-90,-18',
    text: 'Numbers dip after Trump takes office.',
    textOffset: [-175, 0],
  },
  {
    xVal: 0,
    yVal: moment('5/15/2018', 'M/DD/YYYY'),
    path: '',
    text:
      'Around May/June, metering is institutionalized, limiting the number of asylum seekers allowed through ports of entry each day.',
    textOffset: [-175, 0],
  },
  {
    xVal: 0,
    yVal: moment('3/1/2019', 'M/DD/YYYY'),
    path: 'M118,97C52,136,-29,132,-77,87',
    text:
      'Smugglers charge less to transport adults with children who claim asylum, leading to a surge in families arriving at the border.',
    textOffset: [-175, 0],
  },
  {
    xVal: 90000,
    yVal: moment('6/1/2019', 'M/DD/YYYY'),
    path: 'M-53,59C-66,13,-97,0,-139,-1',
    text: 'Numbers dip as migrants are refused entry and sent back to Mexico.',
    textOffset: [-280, -42],
  },
];

let mobileAnnotations = [
  {
    xVal: 18000,
    yVal: moment('4/1/2017', 'M/DD/YYYY'),
    path: 'M-17,-49C6,-58,26,-50,39,-33',
    text: 'Numbers dip after Trump takes office.',
    textOffset: [36, -17],
  },
  {
    xVal: 37000,
    yVal: moment('5/5/2018', 'M/DD/YYYY'),
    path: '',
    text:
      'Around May/June, metering is institutionalized, limiting the number of asylum seekers allowed through ports of entry each day.',
    textOffset: [-56, -29],
  },
  {
    xVal: 10000,
    yVal: moment('3/1/2019', 'M/DD/YYYY'),
    path: 'M50,148C26,135,20,114,24,89',
    text:
      'Smugglers charge less to transport adults with children who claim asylum, leading to a surge in families arriving at the border.',
    textOffset: [20, 7],
  },
  {
    xVal: 90000,
    yVal: moment('6/1/2019', 'M/DD/YYYY'),
    path: 'M-58,106C-52,126,-54,150,-71,164',
    text: 'Numbers dip as migrants are refused entry and sent back to Mexico.',
    textOffset: [-180, 181],
  },
];

// arrow marker, need to append to the svg first to use
svg
  .append('marker')
  .attr('id', 'arrow')
  .attr('viewBox', '-10 -10 20 20')
  .attr('markerWidth', 10)
  .attr('markerHeight', 10)
  .attr('fill', 'hsl(0, 0%, 29%)')
  .attr('orient', 'auto-start-reverse')
  .append('path')
  .attr('d', 'M-6.75,-6.75 L 0,0 L -6.75,6.75');

let swoopySel = svg.append('g').attr('class', 'swoopy-drag-lines');

// resize function, based on current frame width
function resize() {
  let frameWidth = getFrameWidth(),
    frameHeight = 2500;

  if (frameWidth < breakPoint) {
    margin.left = 45;
    margin.right = 10;
  } else {
    margin.left = 175;
    margin.right = 60;
  }

  let chartWidth = frameWidth - margin.left - margin.right,
    chartHeight = frameHeight - margin.bottom;

  svg.attr('width', frameWidth).attr('height', frameHeight);
  g.attr('width', chartWidth).attr(
    'transform',
    `translate(${margin.left},${margin.top})`
  );

  x.range([0, chartWidth]);
  y.range([0, chartHeight]);

  xAxis.call(d3.axisTop(x).tickFormat(xAxisTickFormat));

  yAxis.call(
    d3
      .axisLeft(y)
      .ticks(50)
      .tickFormat(customFormat)
  );

  // check if the tick is a year, if so, make it bold
  d3.selectAll('.timeline-axis.axis.axis--y .tick text').each(function() {
    if (moment(d3.select(this).text(), 'YYYY').month() === 0) {
      d3.select(this).attr('font-weight', 700);
    }
  });

  // add y axis grid lines
  d3.selectAll('.timeline-axis .axis--y .tick line').attr('x1', chartWidth);

  // adjust the stacked area chart based on the new x and y
  area
    .y(d => y(d.data.Month))
    .x0(d => x(d[0]))
    .x1(d => x(d[1]));

  stackedArea.attr('d', area);

  policies
    .selectAll('line')
    .attr('x2', d => {
      if (d.Annotation === 'Trump enters office' && chartWidth - 450 > 0) {
        return frameWidth < breakPoint ? chartWidth - 280 : chartWidth - 450;
      } else {
        return frameWidth < breakPoint ? chartWidth - 160 : chartWidth - 200;
      }
    })
    .attr('y1', d => y(d.Date))
    .attr('y2', d => y(d.Date));

  policyDate
    .attr('dy', 1)
    .attr('x', d => {
      if (d.Annotation === 'Trump enters office' && chartWidth - 450 > 0) {
        return frameWidth < breakPoint ? chartWidth - 270 : chartWidth - 440;
      } else {
        return frameWidth < breakPoint ? chartWidth - 150 : chartWidth - 190;
      }
    })
    .attr('y', d => {
      if (d.Annotation === 'Trump enters office') {
        return y(d.Date) - 15;
      } else {
        return y(d.Date) + 3;
      }
    })
    .attr('font-size', frameWidth < breakPoint ? '12px' : '14px');

  policyText
    .attr('dy', 1)
    .attr('x', d => {
      if (d.Annotation === 'Trump enters office' && chartWidth - 450 > 0) {
        return frameWidth < breakPoint ? chartWidth - 270 : chartWidth - 440;
      } else {
        return frameWidth < breakPoint ? chartWidth - 150 : chartWidth - 190;
      }
    })
    .attr('y', d => {
      if (d.Annotation === 'Trump enters office') {
        return y(d.Date) - 10;
      } else {
        return y(d.Date) + 8;
      }
    })
    .attr('font-size', frameWidth < breakPoint ? '12px' : '14px')
    .call(wrap, frameWidth < breakPoint ? 150 : 250);

  labelElements
    .attr('transform', d => `translate(${x(d.Total)}, ${y(d.Month)})`)
    .attr('display', (d, i) => {
      if (
        (i % 4 == 0) | (i == dataTrump.length - 1) &&
        i !== 0 &&
        frameWidth > breakPoint
      ) {
        return 'normal';
      } else return 'none';
    });

  let swoopy = swoopyDrag()
    .x(function(d) {
      return x(d.xVal);
    })
    .y(function(d) {
      return y(d.yVal);
    })
    .draggable(false)
    .annotations(frameWidth < breakPoint ? mobileAnnotations : annotations);

  swoopySel
    .attr('transform', `translate(${margin.left},${margin.top})`)
    .call(swoopy)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '14px');

  swoopySel.selectAll('text').each(function(d) {
    if (d.yVal.isSame(moment('5/5/2018', 'M/D/YYYY'))) {
      d3.select(this)
        .text('')
        .tspans(wordwrap(d.text, frameWidth < breakPoint ? 20 : 18));
    } else {
      d3.select(this)
        .text('')
        .tspans(wordwrap(d.text, frameWidth < breakPoint ? 25 : 18));
    }
  });

  swoopySel.selectAll('path').attr('marker-start', 'url(#arrow)');

  // add SINGULAR words, and make sure the capitalization is the same as it is in the caption
  let boldWords = [
    'Numbers',
    'dip',
    'Poverty,',
    'families.',
    'families',
    'Families',
    'metering',
  ];

  // bold things
  swoopySel.selectAll('tspan').each(function() {
    let boldArray;

    boldArray = d3
      .select(this)
      .text()
      .split(' ')
      .map(s => {
        // if the tspan's text is found in the boldWords array
        if (boldWords.find(b => s.includes(b))) {
          // if the found text includes 'families', make it yellow
          if (s.toLowerCase().includes('families')) {
            return `<tspan font-weight="900">${boldWords.find(b =>
              s.includes(b)
            )}</tspan>`;
          } else {
            // otherwise, just bold it
            return `<tspan font-weight="900">${boldWords.find(b =>
              s.includes(b)
            )}</tspan>`;
          }
        } else return s; // otherwise, just return the normal text
      });

    d3.select(this).html(boldArray.join(' '));
  });
}

// call resize on load
resize();
window.addEventListener('resize', _.debounce(resize, 200));

// function for text wrapping
function wrap(text, width) {
  text.each(function() {
    let text = d3.select(this),
      words = d3
        .select(this)
        .data()[0]
        .Annotation.split(/\s+/) // split by spaces, tabs, and line breaks
        .reverse(),
      word,
      line = [],
      lineNumber = 0,
      lineHeight = 1.1, // ems
      x = text.attr('x'),
      y = text.attr('y'),
      dy = parseFloat(text.attr('dy')),
      tspan = text
        .text(null)
        .append('tspan')
        .attr('x', x)
        .attr('y', y)
        .attr('dy', dy + 'em');
    while ((word = words.pop())) {
      // loops through the entire array of words
      line.push(word);
      tspan.text(line.join(' '));
      if (tspan.node().getComputedTextLength() > width) {
        // if it's greater than the specified width
        line.pop(); // modified line by getting rid of the last element
        tspan.text(line.join(' ')); // join everything but the last element with a space
        line = [word];
        tspan = text // make another tspan with the word(s) that exceed the width
          .append('tspan')
          .attr('x', x)
          .attr('y', y)
          .attr('dy', ++lineNumber * lineHeight + dy + 'em')
          .text(word);
      }
    }
  });
}
